import { useAuth } from "@/features/auth";
import { ApiClient, useApiClient } from "@/infrastructure/rest";
import { DossierOverzichtEvent, EventReason, useRealtimeTopic } from "@/infrastructure/rsocket";
import { useRemoveCacheListItem, useUpdateCacheListItem, useUpsertCacheListItem } from "@/infrastructure/tanstack-query/hooks";
import { DossierOverzichtDto, DossierOverzichtItemDto } from "@/shared/types";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useCallback } from "react";
import { dossierOverzichtQueryKey, DossierOverzichtStatus } from "./constants";
import { DossierOverzicht } from "./domain";

type PaginatedDossierOverzicht = {
    hasNext: boolean;
    items: DossierOverzicht[];
};

// TODO remove the union response handling once the new backend has been released to prod
type DossierOverzichtResponse = DossierOverzichtDto | DossierOverzichtItemDto[];
function handleDossierOverzichtItemResponse(response: DossierOverzichtResponse | null): PaginatedDossierOverzicht {
    if (response === null) return { hasNext: false, items: [] };
    if (Array.isArray(response)) {
        return { hasNext: false, items: response.map((dossier) => DossierOverzicht.fromDto(dossier)) };
    } else {
        return {
            hasNext: response.pageMetaData.hasNext,
            items: response.items.map((dossier) => DossierOverzicht.fromDto(dossier)),
        };
    }
}

async function fetchDossierOverzicht(apiClient: ApiClient, queryParams: URLSearchParams): Promise<PaginatedDossierOverzicht> {
    const pageResponse = await apiClient.get<DossierOverzichtResponse>(`/dossier-overzicht?${queryParams.toString()}`);
    return handleDossierOverzichtItemResponse(pageResponse);
}

export function useGetDossierOverzicht<T = DossierOverzicht[]>(
    status: DossierOverzichtStatus = "Open",
    select?: (data: DossierOverzicht[]) => T
) {
    const { apiDomain } = useAuth();
    const apiClient = useApiClient();

    return useQuery({
        queryKey: dossierOverzichtQueryKey(status),
        queryFn: async () => {
            const queryParams = getQueryParams({ status });
            const { items } = await fetchDossierOverzicht(apiClient, queryParams);
            return items.sort(DossierOverzicht.sort);
        },
        select,
        enabled: apiDomain !== null,
    });
}

type DossierOverzichtQueryParams = {
    status: DossierOverzichtStatus;
    page?: number;
    search?: string;
};

// creates URLSearchParams with either search or from, otherwise returns empty URLSearchParams
// when search is provided, we don't include the page param because the backend doesn't paginate.
// Instead it always returns one page with hasNext: false
function getQueryParams({ status, page, search }: DossierOverzichtQueryParams): URLSearchParams {
    const queryParams = new URLSearchParams({ status });
    if (search && search.length > 2) {
        queryParams.append("search", search);
        return queryParams;
    }
    if (page) {
        queryParams.append("page", page.toString());
        return queryParams;
    }
    return queryParams;
}

export function useGetGeslotenDossierOverzicht(search: string) {
    const { apiDomain } = useAuth();
    const apiClient = useApiClient();
    const status = "Gesloten";
    const page = 1;

    return useInfiniteQuery({
        queryKey: [...dossierOverzichtQueryKey(status), search].filter(Boolean),
        queryFn: async ({ pageParam }): Promise<PaginatedDossierOverzicht> => {
            const queryParams = getQueryParams({ status, search, page: pageParam.page });
            return await fetchDossierOverzicht(apiClient, queryParams);
        },
        initialPageParam: { page },
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
            if (lastPage !== null && lastPage.hasNext) {
                return { page: lastPageParam.page + 1 };
            }
            return null;
        },
        select: (page) => page.pages.flatMap((p) => p.items),
        enabled: apiDomain !== null,
    });
}

export function useRealtimeDossierOverzicht() {
    const { handleEvent } = useUpdateDossierOverzichtQueriesData();

    useRealtimeTopic<DossierOverzichtEvent>("dossierOverzicht", {
        onMessage: (message) => {
            const { dossier, dossierId, reason } = message;
            handleEvent(dossier, dossierId, reason);
        },
    });
}

type UpdateDossierOverzicht = (dossier: DossierOverzicht) => DossierOverzicht;
type CreateDossierOverzicht = () => DossierOverzicht;

function useUpdateDossierOverzichtQueriesData() {
    const upsertCacheListItem = useUpsertCacheListItem();
    const updateCacheListItem = useUpdateCacheListItem();
    const removeCacheListItem = useRemoveCacheListItem();

    const create = useCallback(
        (createFn: CreateDossierOverzicht) => {
            upsertCacheListItem({
                queryKey: dossierOverzichtQueryKey("Open"),
                idField: "dossierId",
                createFn,
                sortFn: (items) => items.sort(DossierOverzicht.sort),
            });
        },
        [upsertCacheListItem]
    );

    const update = useCallback(
        (dossierId: number, updateFn: UpdateDossierOverzicht) => {
            updateCacheListItem({
                queryKey: dossierOverzichtQueryKey("Open"),
                idField: "dossierId",
                idToUpdate: dossierId,
                updateFn,
            });
        },
        [updateCacheListItem]
    );

    const remove = useCallback(
        (idToDelete: number) => {
            removeCacheListItem<DossierOverzicht, "dossierId">({
                queryKey: dossierOverzichtQueryKey("Open"),
                idField: "dossierId",
                idToDelete,
                sortFn: (items) => items.sort(DossierOverzicht.sort),
            });
        },
        [removeCacheListItem]
    );

    /**
     * Helper to completely handle create, update and delete based on eventReason
     */
    const handleEvent = useCallback(
        (dossier: DossierOverzichtItemDto, dossierId: number, eventReason: EventReason) => {
            switch (eventReason) {
                case "Nieuw":
                    create(() => DossierOverzicht.fromDto(dossier));
                    break;
                case "Wijziging":
                    // Simply override the existing dossier with the dossier from event
                    update(dossierId, () => DossierOverzicht.fromDto(dossier));
                    break;
                case "Verwijderd":
                    remove(dossierId);
                    break;
                default:
                    console.warn("Unsupported dossier overzicht notification type:", eventReason);
                    return;
            }
        },
        [create, remove, update]
    );

    return { handleEvent };
}
